'use client';

/**
 * Copilot export helpers — download a single message or full thread as
 * PDF or DOCX. Backend renders the file (WeasyPrint / python-docx); we
 * fetch as a blob with the auth token, parse the Content-Disposition
 * filename, and trigger a browser save.
 */
import { getAuthToken } from '@/lib/auth/store';
import { filenameFromDisposition, saveBlob } from '@/lib/download';

export type CopilotExportFormat = 'pdf' | 'docx';

interface ExportArgs {
  url: string;
  /** Fallback filename if the server didn't set Content-Disposition. */
  defaultFilename: string;
}

async function downloadBlob({ url, defaultFilename }: ExportArgs): Promise<void> {
  const base = process.env['NEXT_PUBLIC_API_BASE_URL'];
  if (!base) throw new Error('API base URL not configured');
  const token = getAuthToken();
  const res = await fetch(`${base}${url}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) {
    const detail = await res
      .json()
      .catch(() => null)
      .then((d) => (d as { detail?: string } | null)?.detail);
    throw new Error(detail || `Export failed (HTTP ${res.status})`);
  }
  const filename = filenameFromDisposition(res.headers.get('content-disposition'), defaultFilename);
  const blob = await res.blob();
  saveBlob(blob, filename);
}

/** Single assistant or user message → PDF or DOCX. */
export async function downloadMessageExport(
  messageId: string,
  format: CopilotExportFormat,
): Promise<void> {
  await downloadBlob({
    url: `/copilot/messages/${messageId}/export?format=${format}`,
    defaultFilename: `lexai-message-${messageId.slice(0, 8)}.${format}`,
  });
}

/** Whole thread → PDF or DOCX. */
export async function downloadThreadExport(
  threadId: string,
  format: CopilotExportFormat,
): Promise<void> {
  await downloadBlob({
    url: `/copilot/threads/${threadId}/export?format=${format}`,
    defaultFilename: `lexai-thread-${threadId.slice(0, 8)}.${format}`,
  });
}

/**
 * Copy text to the system clipboard. Uses the modern async Clipboard
 * API and falls back to a hidden textarea + execCommand for
 * older/insecure-context browsers.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through */
    }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
